import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, Edit, Save, X, Upload, ShieldCheck, Image as ImageIcon, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = { name: "", image_url: "", min_level: 1 };

export default function AdminBanners() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const fileRef = useRef(null);

  useEffect(() => { if (!loading) { if (!user) navigate("/login"); else if (user.role !== "admin") navigate("/"); } }, [user, loading, navigate]);
  const load = async () => setItems((await api.get("/banners")).data);
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3MB"); return; }
    const r = new FileReader(); r.onload = () => setForm((s) => ({ ...s, image_url: r.result })); r.readAsDataURL(f);
  };

  const submit = async () => {
    try {
      const payload = { ...form, min_level: Number(form.min_level) || 1 };
      if (editing === "new") { await api.post("/admin/banners", payload); toast.success("Banner created"); }
      else { await api.put(`/admin/banners/${editing}`, payload); toast.success("Banner updated"); }
      setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete banner?")) return;
    try { await api.delete(`/admin/banners/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-banners-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Admin</Link>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] font-bold inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Admin</span>
          <h1 className="font-display text-4xl font-black mt-2">Banners</h1>
          <p className="text-zinc-400 text-sm mt-1">Manage profile banners. Set a minimum user level to unlock each.</p>
        </div>
        <button data-testid="new-banner" onClick={() => { setEditing("new"); setForm(empty); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold"><Plus className="w-4 h-4" /> New Banner</button>
      </div>

      {editing && (
        <div className="glass-strong rounded-2xl p-6 mb-6 space-y-3">
          <div className="flex justify-between"><h3 className="font-bold text-lg">{editing === "new" ? "New banner" : "Edit banner"}</h3><button onClick={() => setEditing(null)} className="text-zinc-400"><X className="w-4 h-4" /></button></div>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="bn-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Banner name" className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm" />
            <input data-testid="bn-lvl" type="number" min={1} value={form.min_level} onChange={(e) => setForm({ ...form, min_level: e.target.value })} placeholder="Min level" className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm" />
          </div>
          <div className="flex gap-3 items-center">
            <input data-testid="bn-url" value={form.image_url?.startsWith("data:") ? "" : form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm" />
            <input type="file" accept="image/*" hidden ref={fileRef} onChange={onFile} />
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm font-bold"><Upload className="w-4 h-4" /> Upload</button>
          </div>
          {form.image_url && <img src={form.image_url} alt="" className="w-64 aspect-video object-cover rounded-lg border border-white/10" />}
          <button data-testid="bn-save" onClick={submit} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold text-sm"><Save className="w-4 h-4" /> Save</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} className="glass rounded-2xl overflow-hidden">
            <div className="relative aspect-video"><img src={b.image_url} alt={b.name} className="w-full h-full object-cover" /></div>
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold">{b.name}</h3>
                <span className="text-[10px] font-bold text-[#FF9F0A] uppercase tracking-widest">Lv {b.min_level}+</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(b.id); setForm({ name: b.name, image_url: b.image_url, min_level: b.min_level }); }} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold"><Edit className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => remove(b.id)} className="px-3 py-2 rounded-lg bg-[#E50914]/10 text-[#E50914] hover:bg-[#E50914] hover:text-white text-xs font-bold"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full glass rounded-2xl p-10 text-center text-zinc-500 text-sm flex flex-col items-center gap-2"><ImageIcon className="w-8 h-8 opacity-40" />No banners yet. Add one so users can pick it based on their level.</div>}
      </div>
    </div>
  );
}
